import { Component, inject, Input, ViewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonDirective, ButtonLabel, ButtonModule } from 'primeng/button';
import { Card } from 'primeng/card';
import { AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { Divider } from 'primeng/divider';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { QuoteService } from '../../quote.service';
import { Quote } from '../../../models/quote.model';
import { QuoteRevision } from '../../../models/quote-revision.model';
import { SettingsService } from '../../settings-page/settings-service/settings.service';
import { DrawerModule } from 'primeng/drawer';
import { ProgressBar } from 'primeng/progressbar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { QuillComponent } from '../../quill/quill.component';
import { QuillReadonlyComponent } from '../../quill-readonly/quill-readonly.component';
import { QuoteStatusSelectComponent } from '../../quote-status-select/quote-status-select.component';
import {
  HasDisplayableQuillContentPipe,
  hasDisplayableQuillContent,
} from '../../../pipes/has-displayable-quill-content.pipe';
import { combineLatest, firstValueFrom, map, Observable } from 'rxjs';
import { Tooltip } from 'primeng/tooltip';
import { CustomersService } from '../../services/customers.service';
import { MessageService } from 'primeng/api';
import {
  isInterceptorHandledError,
  parseApiError,
} from '../../quotes-api-error';

interface QuoteInfoPageData {
  quote?: Quote;
  revision?: QuoteRevision;
  isApprovalFlowEnabled: boolean;
  canUseStandardPaymentTerms: boolean;
  canAddStandardPaymentTerms: boolean;
}

@Component({
  selector: 'app-quote-info',
  imports: [
    RouterLink,
    ButtonDirective,
    ButtonLabel,
    Card,
    DatePipe,
    Divider,
    AsyncPipe,
    CurrencyPipe,
    FormsModule,
    InputTextModule,
    DrawerModule,
    ButtonModule,
    ProgressBar,
    RadioButtonModule,
    DatePickerModule,
    SelectModule,
    TextareaModule,
    QuillComponent,
    QuillReadonlyComponent,
    QuoteStatusSelectComponent,
    Tooltip,
    HasDisplayableQuillContentPipe,
  ],
  templateUrl: './quote-info.component.html',
  styleUrl: './quote-info.component.scss',
})
export class QuoteInfoComponent {
  @Input() readOnly: boolean = false;
  quoteService = inject(QuoteService);
  settingsService = inject(SettingsService);
  private messageService = inject(MessageService);

  pageData$: Observable<QuoteInfoPageData> = combineLatest([
    this.quoteService.quote$,
    this.quoteService.revision$,
    this.settingsService.settings$,
  ]).pipe(
    map(([quote, revision, settings]) => {
      const quoteHasPaymentTerms = hasDisplayableQuillContent(
        quote?.payment_terms_delta,
      );
      const settingsHasPaymentTerms = hasDisplayableQuillContent(
        settings?.payment_terms_delta,
      );

      const settingsLoaded = settings !== null;

      return {
        quote,
        revision,
        isApprovalFlowEnabled: settings?.approval_flow_enabled ?? false,
        canUseStandardPaymentTerms:
          !quoteHasPaymentTerms && settingsHasPaymentTerms && settingsLoaded,
        canAddStandardPaymentTerms:
          !quoteHasPaymentTerms && !settingsHasPaymentTerms && settingsLoaded,
      };
    }),
  );

  @ViewChild(QuillComponent) quillComponent!: QuillComponent;

  isEditingReference = false;
  isSavingReference = false;
  referenceDraft = '';

  isDrawerVisible: boolean = false;
  drawerHeader: string = 'Edit';
  drawerContent: 'lead_time' | 'payment_terms' | 'notes' = 'lead_time';
  selectedLeadTime: string = '';
  generalLeadTimeText: string = '';
  calendarLeadTimeDate: Date | null = null;
  selectedWeekOption: string | null = null;
  isSavingLeadTime: boolean = false;
  isSavingEditor: boolean = false;
  isSavingPaymentTerms: boolean = false;
  weekLabels: { [key: string]: string } = {
    '1_week': '1 week',
    '1_2_weeks': '1-2 weeks',
    '2_3_weeks': '2-3 weeks',
    '3_4_weeks': '3-4 weeks',
    over_1_month: 'Over a month',
  };
  get weekOptions() {
    return Object.entries(this.weekLabels).map(([value, label]) => ({
      label,
      value,
    }));
  }

  startEditingReference(quote: Quote) {
    if (this.readOnly) return;
    this.referenceDraft = quote.reference ?? '';
    this.isEditingReference = true;
  }

  cancelReferenceEdit(quote: Quote) {
    this.isEditingReference = false;
    this.referenceDraft = quote.reference ?? '';
  }

  isReferenceUnchanged(quote: Quote): boolean {
    return this.referenceDraft.trim() === (quote.reference ?? '').trim();
  }

  async saveReference(quote: Quote) {
    if (this.isSavingReference || this.isReferenceUnchanged(quote)) {
      this.isEditingReference = false;
      return;
    }

    this.isSavingReference = true;
    const reference = this.referenceDraft.trim();

    try {
      const updatedQuote = await this.quoteService.patchQuote({
        id: quote.id,
        reference,
      });
      this.quoteService.setQuote(updatedQuote);
      this.referenceDraft = updatedQuote.reference ?? '';
      this.isEditingReference = false;
    } catch (error) {
      console.error('Failed to update reference', error);
    } finally {
      this.isSavingReference = false;
    }
  }

  async useStandardPaymentTerms(quote: Quote) {
    if (this.readOnly || this.isSavingPaymentTerms) return;

    this.isSavingPaymentTerms = true;

    try {
      const settings = await firstValueFrom(this.settingsService.settings$);
      if (!settings) {
        return;
      }

      await this.quoteService.patchQuote({
        id: quote.id,
        payment_terms_delta: settings.payment_terms_delta,
        payment_terms_html: settings.payment_terms_html,
      });
      this.isDrawerVisible = false;
    } catch (error) {
      if (!isInterceptorHandledError(error)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: parseApiError(
            error,
            'Failed to apply standard payment terms',
          ),
        });
      }
    } finally {
      this.isSavingPaymentTerms = false;
    }
  }

  openDrawer(content: 'lead_time' | 'payment_terms' | 'notes') {
    if (this.readOnly) return;
    this.drawerContent = content;
    if (content === 'lead_time') {
      this.drawerHeader = 'Edit Lead Time';
    } else if (content === 'payment_terms') {
      this.drawerHeader = 'Edit Payment Terms';
    } else if (content === 'notes') {
      this.drawerHeader = 'Edit Notes';
    }
    this.isDrawerVisible = true;
  }

  editLeadTime(quote: Quote) {
    if (this.readOnly) return;
    if (quote.lead_time_weeks != null) {
      this.selectedLeadTime = 'weeks';
    } else if (quote.lead_time_calendar != null) {
      this.selectedLeadTime = 'calendar';
    } else {
      this.selectedLeadTime = 'general';
    }
    this.generalLeadTimeText = quote.lead_time_general ?? '';
    this.calendarLeadTimeDate = quote.lead_time_calendar
      ? new Date(quote.lead_time_calendar)
      : null;
    this.selectedWeekOption = quote.lead_time_weeks ?? null;

    this.openDrawer('lead_time');
  }

  async saveLeadTime(quote: Quote) {
    if (this.isSavingLeadTime) {
      return;
    }

    this.isSavingLeadTime = true;

    try {
      if (this.selectedLeadTime === 'general') {
        await this.quoteService.patchQuote({
          id: quote.id,
          lead_time_general: this.generalLeadTimeText,
          lead_time_calendar: null,
          lead_time_weeks: null,
        });
      } else if (this.selectedLeadTime === 'calendar') {
        await this.quoteService.patchQuote({
          id: quote.id,
          lead_time_calendar: this.calendarLeadTimeDate,
          lead_time_general: null,
          lead_time_weeks: null,
        });
      } else {
        await this.quoteService.patchQuote({
          id: quote.id,
          lead_time_weeks: this.selectedWeekOption,
          lead_time_general: null,
          lead_time_calendar: null,
        });
      }
      this.isDrawerVisible = false;
    } catch (error) {
      console.error('Failed to update lead time', error);
    } finally {
      this.isSavingLeadTime = false;
    }
  }

  async saveEditorContent(quote: Quote) {
    if (this.isSavingEditor) {
      return;
    }

    this.isSavingEditor = true;

    try {
      if (this.quillComponent) {
        const { delta, html } = this.quillComponent.getContent();
        const patch: Partial<Quote> = { id: quote.id };

        if (this.drawerContent === 'payment_terms') {
          patch.payment_terms_delta = delta;
          patch.payment_terms_html = html;
        } else if (this.drawerContent === 'notes') {
          patch.notes_delta = delta;
          patch.notes_html = html;
        }

        await this.quoteService.patchQuote(patch);
        this.isDrawerVisible = false;
      }
    } catch (error) {
      console.error('Failed to update editor content', error);
    } finally {
      this.isSavingEditor = false;
    }
  }

  // Company selector
  private customersService = inject(CustomersService);
  companies$ = this.customersService.companies$;
  isSavingCompany = false;

  constructor() {
    this.customersService.loadCompanies();
  }

  async onCompanyChange(companyId: number | null, quote: Quote) {
    if (this.isSavingCompany) return;
    this.isSavingCompany = true;
    try {
      const updatedQuote = await this.quoteService.patchQuote({
        id: quote.id,
        company: companyId,
      });
      this.quoteService.setQuote(updatedQuote);
    } catch (error) {
      console.error('Failed to update company', error);
    } finally {
      this.isSavingCompany = false;
    }
  }
}
